import { Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, QueryList, Renderer2, SimpleChanges, ViewChildren } from '@angular/core';
import {
  HOME_RATE_SHIMMER_BASE_COLOR, HOME_RATE_SHIMMER_COLORS, HOME_RATE_SHIMMER_GLINT_MARGIN, HOME_RATE_SHIMMER_GLINT_SIGMA, HOME_RATE_SHIMMER_PAUSE_MS,
  HOME_RATE_SHIMMER_SWEEP_MS, HOME_RATE_SHIMMER_ZOOM
} from '../../model/types/home-revamp-types';

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function rgb(channels: Rgb): string {
  return `rgb(${Math.round(channels[0])}, ${Math.round(channels[1])}, ${Math.round(channels[2])})`;
}

const BASE_RGB = hexToRgb(HOME_RATE_SHIMMER_BASE_COLOR);
const FROM_RGB = hexToRgb(HOME_RATE_SHIMMER_COLORS[0]);
const TO_RGB = hexToRgb(HOME_RATE_SHIMMER_COLORS[1]);

@Component({
  selector: 'rmt-shimmer-text',
  templateUrl: 'rmt-shimmer-text.html'
})
export class RmtShimmerTextComponent implements OnChanges, OnDestroy {
  @Input() text: string = '';
  @Input() loop: boolean = true;
  @Input() playToken: number = 0;
  @ViewChildren('glyphEl') glyphRefs: QueryList<ElementRef>;
  readonly baseColor: string = HOME_RATE_SHIMMER_BASE_COLOR;
  glyphs: string[] = [];
  glintPos: number = null;
  private rafId: number = null;
  private pauseTimer: number = null;
  private startTs: number = 0;
  private reducedMotionQuery: MediaQueryList = null;
  private readonly onReducedMotionChange = () => {
    if (this.reducedMotion) {
      this.stop();
      this.glintPos = null;
      this.paint();
    }
  }

  constructor(private zone: NgZone, private renderer: Renderer2) {
    if (typeof window.matchMedia === 'function') {
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.reducedMotionQuery && typeof this.reducedMotionQuery.addListener === 'function')
        this.reducedMotionQuery.addListener(this.onReducedMotionChange);
    }
  }

  get reducedMotion(): boolean {
    return !!this.reducedMotionQuery && this.reducedMotionQuery.matches;
  }

  trackGlyph(index: number, glyph: string): string {
    return `${index}-${glyph}`;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.text) {
      this.glyphs = Array.from(this.text || '');
      if (this.loop)
        this.sweep();
    }
    if (changes.playToken && !changes.playToken.firstChange && !this.loop)
      this.sweep();
  }

  ngOnDestroy() {
    this.stop();
    if (this.reducedMotionQuery && typeof this.reducedMotionQuery.removeListener === 'function')
      this.reducedMotionQuery.removeListener(this.onReducedMotionChange);
  }

  glyphStyle(index: number): { color: string; transform: string } {
    const g = this.glintPos;
    if (g === null || this.reducedMotion)
      return { color: HOME_RATE_SHIMMER_BASE_COLOR, transform: null };
    const n = this.glyphs.length || 1;
    const x = (index + 0.5) / n;
    const intensity = Math.exp(-Math.pow(x - g, 2) / (2 * Math.pow(HOME_RATE_SHIMMER_GLINT_SIGMA, 2)));
    const gradient: Rgb = [lerp(FROM_RGB[0], TO_RGB[0], x), lerp(FROM_RGB[1], TO_RGB[1], x), lerp(FROM_RGB[2], TO_RGB[2], x)];
    const color: Rgb = [lerp(BASE_RGB[0], gradient[0], intensity), lerp(BASE_RGB[1], gradient[1], intensity), lerp(BASE_RGB[2], gradient[2], intensity)];
    return { color: rgb(color), transform: `scale(${1 + (HOME_RATE_SHIMMER_ZOOM - 1) * intensity})` };
  }

  private paint() {
    if (!this.glyphRefs)
      return;
    this.glyphRefs.forEach((ref, index) => {
      const style = this.glyphStyle(index);
      this.renderer.setStyle(ref.nativeElement, 'color', style.color);
      if (style.transform)
        this.renderer.setStyle(ref.nativeElement, 'transform', style.transform);
      else
        this.renderer.removeStyle(ref.nativeElement, 'transform');
    });
  }

  private stop() {
    if (this.rafId !== null) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.pauseTimer !== null) {
      window.clearTimeout(this.pauseTimer);
      this.pauseTimer = null;
    }
  }

  private frame(now: number) {
    if (!this.startTs)
      this.startTs = now;
    const progress = (now - this.startTs) / HOME_RATE_SHIMMER_SWEEP_MS;
    if (progress >= 1) {
      this.glintPos = null;
      this.startTs = 0;
      this.rafId = null;
      this.paint();
      if (this.loop)
        this.pauseTimer = window.setTimeout(() => this.sweep(), HOME_RATE_SHIMMER_PAUSE_MS);
      return;
    }
    this.glintPos = -HOME_RATE_SHIMMER_GLINT_MARGIN + progress * (1 + 2 * HOME_RATE_SHIMMER_GLINT_MARGIN);
    this.paint();
    this.rafId = window.requestAnimationFrame((ts: number) => this.frame(ts));
  }

  private sweep() {
    if (this.reducedMotion)
      return;
    this.stop();
    this.startTs = 0;
    this.rafId = this.zone.runOutsideAngular(() => window.requestAnimationFrame((ts: number) => this.frame(ts)));
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-shimmer-text/rmt-shimmer-text.ts